import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import EditorialMarquee from "../components/EditorialMarquee";
import { JsonLd } from "../components/SEO";
import { SERVICES } from "../data/services";

const servicesSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Digital Marketing Services | Dorado9",
  url: "https://dorado9.com/services",
  description:
    "Explore SEO, Google Ads, social media, branding, web development and digital growth services designed for ambitious businesses.",
  hasPart: SERVICES.map((service) => ({
    "@type": "Service",
    name: service.title,
    url: `https://dorado9.com/services/${service.slug}`,
    provider: { "@id": "https://dorado9.com/#organization" },
  })),
};

export default function Services() {
  const [active, setActive] = useState(SERVICES[0].slug);
  const [cursor, setCursor] = useState({ x: 0, y: 0, visible: false });
  const wrapRef = useRef(null);

  const activeItem = SERVICES.find((s) => s.slug === active) || SERVICES[0];

  const onMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top, visible: true });
  };

  return (
    <>
      <JsonLd id="services-page" data={servicesSchema} />
      {/* HERO */}
      <section className="relative pt-44 lg:pt-71.8 pb-44 lg:pb-20 px-6 lg:px-12 bg-brand-bg overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1920&q=60&auto=format&fit=crop"
            alt=""
            aria-hidden="true"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover opacity-[0.18] mix-blend-luminosity"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,18,22,0.72) 0%, rgba(20,18,22,0.55) 45%, var(--bg) 100%)" }} />
        </div>
        <div className="absolute inset-0 grain opacity-25" />
        <div className="absolute -top-40 -right-40 w-[60vw] h-[60vw] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(102,76,221,0.34), transparent 60%)" }} />
        <div className="absolute -bottom-40 -left-40 w-[45vw] h-[45vw] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(17,245,186,0.10), transparent 65%)" }} />

        <div className="max-w-[1600px] mx-auto relative">
  <div className="max-w-6xl">
    <h1 className="eyebrow mb-6 flex items-center gap-3">
      <span className="w-8 h-px bg-brand-purple" />
      Services · 07 Pillars
    </h1>

    <p
      className="font-display text-[13vw] sm:text-[10vw] lg:text-[8.5vw] leading-[1.295] tracking-tighter uppercase text-white"
      data-testid="services-hero-title"
    >
      <MaskedLines
        lines={[
          <>Seven Services.</>,
          <>One <span className="font-serif-accent text-brand-purple lowercase">growth engine.</span></>,
        ]}
        stagger={0.14}
        delay={0.25}
      />
    </p>

    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.85 }}
      className="mt-10 max-w-4xl text-zinc-400 text-lg leading-relaxed"
    >
      Whether you need a single service or a complete growth partner, every solution is designed to complement the others. From SEO, Google Ads, Meta Ads, branding, and web development to content strategy, analytics, and conversion optimization, each discipline works together to create a marketing system that attracts the right audience, generates qualified leads, and drives sustainable business growth. Start with one service today. Scale into a complete growth engine tomorrow.
    </motion.p>
  </div>
</div>
      </section>

      <EditorialMarquee />

      {/* INTERACTIVE INDEX */}
      <section
        ref={wrapRef}
        onMouseMove={onMove}
        onMouseLeave={() => setCursor((c) => ({ ...c, visible: false }))}
        className="relative bg-brand-bg py-16 lg:py-24 px-6 lg:px-12"
      >
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-6 lg:gap-16 relative">
          {/* Left: numbered list */}
          <div className="lg:col-span-8 relative z-10">
            <ul>
              {SERVICES.map((s, i) => (
                <li key={s.slug} data-testid={`services-index-${s.slug}`}>
                  <Link
                    to={`/services/${s.slug}`}
                    onMouseEnter={() => setActive(s.slug)}
                    className="group grid grid-cols-12 items-center gap-4 py-6 lg:py-8 border-b border-[color:var(--border)] hover:border-brand-mint/60 transition-colors duration-500"
                  >
                    <span className="col-span-2 lg:col-span-1 font-serif-accent text-brand-mint text-3xl lg:text-5xl leading-none">
                      {s.n}
                    </span>
                    <span className={`col-span-8 lg:col-span-9 font-display text-3xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tighter uppercase transition-all duration-500 ${
                      active === s.slug ? "text-white translate-x-2 lg:translate-x-4" : "text-zinc-500 group-hover:text-white"
                    }`}>
                      {s.title}
                    </span>
                    <span className="col-span-2 lg:col-span-2 flex justify-end">
                      <span className={`inline-flex items-center gap-1 text-xs uppercase tracking-[0.24em] transition-all duration-500 ${
                        active === s.slug ? "text-brand-mint" : "text-zinc-600"
                      }`}>
                        <span className="hidden lg:inline">Explore</span> <ArrowUpRight size={16} />
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: sticky preview image */}
          <div className="hidden lg:block lg:col-span-4">
            <div className="sticky top-32">
              <div className="relative aspect-[4/5] overflow-hidden border border-[color:var(--border)]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={activeItem.slug}
                    src={activeItem.hero_image}
                    alt={activeItem.title}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="services-preview-image"
                    style={{
    objectPosition: activeItem.hero_position || "35% 50%",
  }}
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="eyebrow mb-2">{activeItem.n} — {activeItem.short}</p>
                  <p className="font-display text-2xl text-white uppercase leading-tight">{activeItem.tagline}</p>
                </div>
              </div>
              <p className="mt-6 text-zinc-400 text-sm leading-relaxed">{activeItem.intro}</p>
              <Link to={`/services/${activeItem.slug}`} className="mt-6 btn-primary text-sm" data-testid="services-preview-cta">
                View the deck <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>

        {/* Cursor follow chip */}
        <AnimatePresence>
          {cursor.visible && (
            <motion.div
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7 }}
              className="pointer-events-none hidden lg:flex absolute z-20 items-center gap-2 px-4 py-2 rounded-full bg-brand-mint text-[color:var(--bg)] text-xs uppercase tracking-[0.24em] font-bold"
              style={{ left: cursor.x + 24, top: cursor.y + 20 }}
            >
              View <ArrowUpRight size={12} />
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* CROSS-SELL / MANIFESTO STRIP */}
      <section className="relative bg-brand-bg py-24 lg:py-32 px-6 lg:px-12 ">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="eyebrow mb-6">The process</p>

<h2 className="font-display text-4xl lg:text-6xl leading-[0.95] tracking-tight uppercase">
  Four steps. <span className="font-serif-accent text-brand-mint lowercase">One outcome.</span>
</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            {[
  {
    t: "DISCOVER.",
    d: "Every project starts with understanding your business, audience, competitors, and goals. No assumptions. No cookie-cutter strategies."
  },
  {
    t: "STRATEGIZE.",
    d: "Together we build a roadmap that prioritizes the work with the highest impact first, instead of trying to do everything at once."
  },
  {
    t: "EXECUTE.",
    d: "Whether it's SEO, paid ads, branding, content or development, every deliverable is built to support measurable business growth."
  },
  {
    t: "OPTIMIZE.",
    d: "We measure, learn, improve, and repeat. Marketing isn't a one-time launch—it's an ongoing process of refinement."
  },
].map((r, i) => (
              <Reveal key={r.t} delay={i * 0.06}>
                <div className="grid grid-cols-12 gap-4 py-6 border-b border-[color:var(--border)]">
                  <span className="col-span-1 font-serif-accent text-brand-mint text-2xl leading-none">✦</span>
                  <span className="col-span-11 lg:col-span-5 font-display text-xl lg:text-2xl uppercase">{r.t}</span>
                  <span className="col-span-12 lg:col-span-6 text-zinc-400 leading-relaxed">{r.d}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-brand-bg py-26 lg:py-8 px-6 lg:px-12 text-center">
        <Reveal>
          <p className="eyebrow mb-6">YOUR NEXT CHAPTER STARTS HERE</p>
          <h2 className="font-display text-4xl lg:text-7xl leading-[0.9] tracking-tighter uppercase max-w-4xl mx-auto">
           READY TO
OUTGROW <span className="font-serif-accent text-brand-mint lowercase"><br/>your competition?</span>
          </h2>
          <Link to="/contact" className="btn-primary mt-10 inline-flex" data-testid="services-final-cta">
            Book a consultation <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
